import { useEffect } from 'react';
import * as sentry from '@ardoq/sentry';
import {
  DynamicMetadataFn,
  Team,
  Transaction,
  TransactionOptions,
} from './types';
import {
  endPerformanceMeasurement,
  startPerformanceMeasurement,
} from './measurements';
import * as offlineDetection from './offlineDetection';
import * as documentVisibilityDetection from './documentVisibilityDetection';
import { currentTimestamp } from '@ardoq/date-time';

let config: {
  buildVersion: string;
  dynamicMetadataFn: DynamicMetadataFn;
};

export const noOpTransaction: Transaction = {
  name: 'dummy',
  threshold: 0,
  sentryTransaction: null,
  startTime: 0,
  team: Team.CORE,
};

/**
 * Starts a profiling transaction.
 *
 * Use transactions to measure duration of high-level user-initiated operations like
 * opening a workspace or switching views. For sub-operations that are part of a transaction
 * , see the API in `span.ts`.
 *
 * All transaction measurements are available in Kibana. Additionally, a percentage
 * of transactions is also uploaded to Sentry. The percentage is controller when
 * configuring the `ardoq/sentry` package. Spans are uploaded to Sentry only.
 *
 * Pass the result of this function, with optional viewId and metadata, to `endTransaction`.
 *
 * Note that it might be better to use `startSpan` to create a child span
 * of a transaction if one is already in progress.
 *
 * See README.md for more details.
 */
export const startTransaction = (
  name: string,
  threshold: number,
  team: Team
): Transaction => {
  if (!config) return noOpTransaction;

  startPerformanceMeasurement(name);
  return {
    name,
    threshold,
    sentryTransaction: sentry.startTransaction(name),
    startTime: currentTimestamp(),
    team,
  };
};

/**
 * See documentation for `startTransaction`.
 */
export const endTransaction = (
  transaction: Transaction,
  { metadata, viewId }: TransactionOptions = {}
) => {
  if (!config) return;

  const browserWentOffline = offlineDetection.browserWentOffline(
    transaction.startTime
  );
  const documentVisibilityRatio =
    documentVisibilityDetection.documentVisibilityRatio(transaction.startTime);
  const isInIframe = window.self !== window.top;

  sentry.endTransaction(transaction.sentryTransaction, {
    browserWentOffline,
    documentVisibilityRatio,
    isInIframe,
  });

  if (config.buildVersion) {
    endPerformanceMeasurement({
      name: transaction.name,
      threshold: transaction.threshold,
      buildVersion: config.buildVersion,
      dynamicMetadataFn: config.dynamicMetadataFn,
      viewId,
      metadata,
      browserWentOffline,
      documentVisibilityRatio,
      isInIframe,
      team: transaction.team,
    });
  }
};

/**
 * Initialize the package. Has to be called before calling any other functions.
 */
export const initialize = (
  buildVersion: string,
  dynamicMetadataFn = () => ({})
) => {
  config = {
    buildVersion,
    dynamicMetadataFn,
  };
  offlineDetection.initialize();
  documentVisibilityDetection.initialize();
};

/**
 * Use to measure render time of a React component.
 *
 * Note that it might be better to use `useSpan` to create a child span
 * of a transaction if one is already in progress.
 */
export const useTransaction = (
  name: string,
  threshold: number,
  options: TransactionOptions = {},
  team: Team
) => {
  const transaction = startTransaction(name, threshold, team);
  useEffect(() => endTransaction(transaction, options));
};

/**
 * Wraps a function with a transaction.
 *
 * The result is a function that expects the same arguments and returns
 * the same value as `functionToBeMeasured`.
 *
 * Use `functionWithTransactionAsync` for async functions.
 *
 * Note that this function does *NOT* call `functionToBeMeasured`. You probably
 * want `runWithTransaction` if you need that.
 *
 * Example usage:
 *
 * export const getMergedMultiEntityProperties = profiling.functionWithTransaction(
 *   'merge entity properties',
 *   1000,
 *   (propertiesPerEntity: EditorProperty[][]) => {
 *     return zip(...propertiesPerEntity).map(mergeProperties);
 *   }
 * );
 *
 * Then every `getMergedMultiEntityProperties` call will be wrapped with
 * a transaction.
 */
export const functionWithTransaction = <F extends (...args: any[]) => any>(
  name: string,
  threshold: number,
  functionToBeMeasured: F,
  team: Team
): ((...args: Parameters<F>) => ReturnType<F>) => {
  const functionWithMeasurement = (...parameters: Parameters<F>) => {
    const transaction = startTransaction(name, threshold, team);
    const result = functionToBeMeasured(...parameters);
    endTransaction(transaction);
    return result;
  };
  return functionWithMeasurement;
};

/**
 * Wraps an async function with a transaction.
 *
 * See documentation for `functionWithTransaction` for more details.
 */
export const functionWithTransactionAsync = <
  F extends (...args: any[]) => Promise<any>,
>(
  name: string,
  threshold: number,
  functionToBeMeasured: F,
  team: Team
): ((...args: Parameters<F>) => Promise<Awaited<ReturnType<F>>>) => {
  const functionWithMeasurement = async (...parameters: Parameters<F>) => {
    const transaction = startTransaction(name, threshold, team);
    const result = await functionToBeMeasured(...parameters);
    endTransaction(transaction);
    return result;
  };
  return functionWithMeasurement;
};

/**
 * Wraps a block of code with a transaction, returning the result of that code.
 *
 * This function is intended to be used for profiling blocks of code without
 * extracting them into functions. `functionToBeMeasured` expects no arguments.
 *
 * Note that it might be better to use `inSpan` to create a child span
 * of a transaction if one is already in progress.
 *
 * Use `runWithTransactionAsync` for async functions.
 *
 * Example usage:
 *
 * profiling.runWithTransaction(transaction, 'setup continuous fetch', () =>
 *   setupContinuousFetch()
 * );
 */
export const runWithTransaction = <F extends () => any>(
  name: string,
  threshold: number,
  functionToBeMeasured: F,
  team: Team
): ReturnType<F> => {
  const transaction = startTransaction(name, threshold, team);
  const result = functionToBeMeasured();
  endTransaction(transaction);
  return result;
};

/**
 * Wraps an async function with a transaction.
 *
 * See documentation for `runWithTransaction` for more details.
 */
export const runWithTransactionAsync = async <F extends () => Promise<any>>(
  name: string,
  threshold: number,
  functionToBeMeasured: F,
  team: Team
): Promise<Awaited<ReturnType<F>>> => {
  const transaction = startTransaction(name, threshold, team);
  const result = await functionToBeMeasured();
  endTransaction(transaction);
  return result;
};
